import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { PuntoElegido, SelectorUbicacionComponent } from "src/app/shared/mapa/selector-ubicacion.component";
import { Ubicacion } from "src/app/shared/model/asistencia/asistencia.model";
import { Cliente } from "src/app/shared/model/ajustesUsuario/cliente.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { GestionclientesService } from "src/app/clientes/gestionclientes.service";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../../asistencia.service";
import { EstadoEditorSitio, FiltrosSitios, RUTA_SITIOS } from "../sitios.rutas";

/**
 * Alta y edicion de un sitio, como pagina (asistencia/ubicaciones/alta y
 * asistencia/ubicaciones/edicion/:id). Era un dialogo: con el mapa dentro
 * no cabia en la ventana, el encabezado quedaba detras de la barra
 * superior y los botones fuera de la vista.
 *
 * Las tres decisiones que se toman aqui no son de forma:
 *
 *  - El TIPO separa a la gente propia de la asignada a clientes. Si es
 *    sitio de cliente, el cliente que se elija es el que después recibe
 *    su lista de asistencia; sin él, el sitio nunca aparece en ninguna.
 *  - El CANAL decide si allí se checa con huella, con el teléfono o de
 *    las dos formas. En la mayoría de los sitios de cliente no habrá
 *    kiosco.
 *  - La GEOCERCA solo se puede exigir con centro y radio capturados. El
 *    servidor lo rechaza igual; aquí se evita que alguien lo descubra
 *    con un error después de llenar el formulario.
 */
@Component({
  selector: "app-editor-ubicacion",
  standalone: true,
  templateUrl: "./editor-ubicacion.component.html",
  styleUrls: ["./editor-ubicacion.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    SelectorUbicacionComponent,
  ],
})
export class EditorUbicacionComponent implements OnInit, OnDestroy {
  ubicacion: Ubicacion = this.conValoresPorOmision(new Ubicacion());
  /** Mientras se vuelve a consultar el sitio tras recargar la pagina. */
  cargando = false;
  guardando = false;

  /** Lo que se ve en el combo: los clientes que casan con el buscador. */
  clientesFiltrados: Cliente[] = [];
  buscadorCliente = new FormControl("");

  /** Las que de verdad se usan; la lista completa sería ruido. */
  readonly zonas = [
    "America/Mexico_City",
    "America/Cancun",
    "America/Chihuahua",
    "America/Hermosillo",
    "America/Tijuana",
    "America/Mazatlan",
    "America/Merida",
    "America/Monterrey",
  ];

  /** Radio mínimo; por debajo manda el error del GPS, no la geocerca. */
  readonly radioMinimo = 50;
  /** Radio con el que arranca la geocerca al señalar el sitio: una bodega mediana. */
  readonly radioInicial = 100;

  private clientes: Cliente[] = [];
  private filtrosLista: FiltrosSitios | null = null;
  private readonly suscripciones = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly asistenciaService: AsistenciaService,
    private readonly clientesService: GestionclientesService,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const estado = (history.state ?? {}) as EstadoEditorSitio;
    this.filtrosLista = estado.filtros ?? null;

    const id = Number(this.route.snapshot.paramMap.get("id"));
    if (id > 0) {
      if (estado.ubicacion?.id === id) {
        this.ubicacion = this.conValoresPorOmision({ ...estado.ubicacion });
      } else {
        this.cargarSitio(id);
      }
    }

    this.cargarClientes();
    this.suscripciones.add(this.buscadorCliente.valueChanges.subscribe(() => this.filtrarClientes()));
  }

  ngOnDestroy(): void {
    this.suscripciones.unsubscribe();
  }

  get esSitioDeCliente(): boolean {
    return this.ubicacion.tipo === "SITIO_CLIENTE";
  }

  get esNuevo(): boolean {
    return !this.ubicacion.id;
  }

  get titulo(): string {
    return this.esNuevo ? "Nuevo sitio" : "Editar sitio";
  }

  /**
   * Al dejar de ser sitio de cliente se limpia el cliente. Dejarlo
   * puesto haría que la lista de ese cliente incluyera gente que nunca
   * pisa su sitio; el servidor también lo descarta, pero la pantalla no
   * debe mostrar un dato que no se va a guardar.
   */
  alCambiarTipo(): void {
    if (!this.esSitioDeCliente) {
      this.ubicacion.idCliente = null;
    }
  }

  /** Del mapa: el punto elegido. Si aún no hay radio, se propone uno para que el círculo se vea. */
  alElegirPunto(punto: PuntoElegido): void {
    this.ubicacion.latitud = punto.latitud;
    this.ubicacion.longitud = punto.longitud;
    if (!this.ubicacion.radioMetros) {
      this.ubicacion.radioMetros = this.radioInicial;
    }
    this.cdr.detectChanges();
  }

  guardar(): void {
    const problema = this.primerProblema();
    if (problema) {
      this.msg.warningCall(problema, null, null);
      return;
    }

    this.guardando = true;
    this.cdr.detectChanges();

    this.asistenciaService.guardarUbicacion(this.ubicacion).subscribe({
      next: (respuesta) => {
        this.guardando = false;
        // El negocio devuelve el sitio guardado; si algo lo rechazó,
        // devuelve el motivo y sin id.
        if (!respuesta?.id) {
          this.msg.warningCall(respuesta?.mensaje || "No se pudo guardar el sitio.", null, null);
          this.cdr.detectChanges();
          return;
        }
        this.msg.successCall("El sitio quedó guardado.", null, () => this.volver());
      },
      error: () => {
        this.guardando = false;
        this.msg.errorCall("No se pudo guardar el sitio.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  /** De regreso al catalogo, con los filtros que traia. */
  volver(): void {
    this.router.navigate([RUTA_SITIOS], {
      state: this.filtrosLista ? { filtros: this.filtrosLista } : undefined,
    });
  }

  // ------------------------------------------------------------------

  /** El primer motivo por el que no se puede guardar, o null. */
  private primerProblema(): string | null {
    if (!this.ubicacion.nombre?.trim()) {
      return "Escriba el nombre del sitio.";
    }
    if (!this.ubicacion.tipo) {
      return "Indique si es oficina propia, sitio de cliente o home office.";
    }
    if (this.esSitioDeCliente && !this.ubicacion.idCliente) {
      return "Elija el cliente al que pertenece el sitio.";
    }
    if (!this.ubicacion.exigeGeocerca) {
      return null;
    }
    if (this.ubicacion.latitud == null || this.ubicacion.longitud == null) {
      return "Para exigir geocerca hay que señalar el sitio en el mapa.";
    }
    if (!this.ubicacion.radioMetros || this.ubicacion.radioMetros < this.radioMinimo) {
      return `El radio de la geocerca no puede ser menor a ${this.radioMinimo} metros.`;
    }
    return null;
  }

  private conValoresPorOmision(ubicacion: Ubicacion): Ubicacion {
    if (!ubicacion.canalRegistro) {
      ubicacion.canalRegistro = "KIOSCO";
    }
    if (!ubicacion.zonaHoraria) {
      ubicacion.zonaHoraria = "America/Mexico_City";
    }
    return ubicacion;
  }

  /**
   * Al recargar la pagina el sitio ya no viene en history.state: se
   * consulta el catalogo completo, bajas incluidas, y se toma el que toca.
   * El catalogo de una empresa es corto, y asi no hace falta un endpoint
   * nuevo por id.
   */
  private cargarSitio(id: number): void {
    this.cargando = true;
    const filtro = new Ubicacion();
    filtro.estatus = "INACTIVO";
    this.asistenciaService
      .consultarUbicaciones(filtro)
      .pipe(
        finalize(() => {
          this.cargando = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          const sitio = (respuesta?.list ?? []).find((s) => s.id === id);
          if (!sitio) {
            this.msg.warningCall("El sitio no existe o es de otra empresa.", null, () => this.volver());
            return;
          }
          this.ubicacion = this.conValoresPorOmision({ ...sitio });
          this.filtrarClientes();
        },
        error: () => {
          this.msg.errorCall("No se pudo consultar el sitio.", null, () => this.volver());
        },
      });
  }

  /**
   * filtroConsultaClientes.do pagina con LIMIT :regInicial, :regXPagina:
   * sin los dos numeros el SQL truena y el servidor responde lista vacia
   * sin avisar. El dialogo anterior no los mandaba, y por eso el combo
   * salia sin clientes. Se piden hasta 1000 de una vez (el mismo tope que
   * Proyectos) y el buscador filtra en el navegador.
   */
  private cargarClientes(): void {
    const filtro = new ConsultaList<Cliente>();
    filtro.bean = new Cliente();
    filtro.regInicial = 0;
    filtro.regXPagina = 1000;
    this.clientesService.filtroBusquedaCliente(filtro).subscribe({
      next: (respuesta) => {
        this.clientes = respuesta?.list ?? [];
        this.filtrarClientes();
      },
      error: () => {
        this.clientes = [];
        this.filtrarClientes();
      },
    });
  }

  /**
   * Un cliente dado de baja ya no recibe listas, asi que no se ofrece;
   * se deja solo si es el que ya tenia el sitio, para que la edicion no
   * lo pierda en silencio.
   */
  private filtrarClientes(): void {
    const texto = (this.buscadorCliente.value ?? "").toString().trim().toLowerCase();
    this.clientesFiltrados = this.clientes.filter((cliente) => {
      if (cliente.estatus === "BAJA" && cliente.id !== this.ubicacion.idCliente) {
        return false;
      }
      if (!texto) {
        return true;
      }
      return `${cliente.razonsocial ?? ""} ${cliente.rfc ?? ""}`.toLowerCase().includes(texto);
    });
    this.cdr.detectChanges();
  }
}
